import { type RankingItem, type MatchupResult } from '../types';
import { sortByRank } from './ranking';

/**
 * Export to Markdown format
 */
export function exportToMarkdown(
  items: RankingItem[],
  matchupHistory: MatchupResult[]
): string {
  const ranked = sortByRank(items);
  const lines: string[] = [];
  
  lines.push('# Final Prioritization\n');
  lines.push('## Ranked List\n');
  
  ranked.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.name} (${item.wins} wins)`);
  });
  
  lines.push('\n## Raw Scores\n');
  ranked.forEach((item) => {
    lines.push(`- ${item.name}: ${item.wins}`);
  });
  
  if (matchupHistory.length > 0) {
    lines.push('\n## Matchup History\n');
    matchupHistory.forEach((matchup, index) => {
      const item1 = items.find((i) => i.id === matchup.pair[0]);
      const item2 = items.find((i) => i.id === matchup.pair[1]);
      const winner = items.find((i) => i.id === matchup.winner);
      if (item1 && item2 && winner) {
        lines.push(`${index + 1}. ${item1.name} vs ${item2.name} → **${winner.name}**`);
      }
    });
  }
  
  return lines.join('\n');
}

/**
 * Export to JSON format
 */
export function exportToJSON(
  items: RankingItem[],
  matchupHistory: MatchupResult[]
): string {
  const ranked = sortByRank(items);
  const data = {
    prioritized: ranked.map((item) => item.name),
    scores: ranked.reduce((acc, item) => {
      acc[item.name] = item.wins;
      return acc;
    }, {} as Record<string, number>),
    matchupHistory: matchupHistory.map((matchup) => {
      const item1 = items.find((i) => i.id === matchup.pair[0]);
      const item2 = items.find((i) => i.id === matchup.pair[1]);
      const winner = items.find((i) => i.id === matchup.winner);
      return {
        pair: [item1?.name || matchup.pair[0], item2?.name || matchup.pair[1]],
        winner: winner?.name || matchup.winner,
        timestamp: matchup.timestamp,
      };
    }),
    metadata: {
      totalItems: items.length,
      totalComparisons: matchupHistory.length,
      exportedAt: new Date().toISOString(),
    },
  };
  
  return JSON.stringify(data, null, 2);
}

/**
 * Export to plaintext format
 */
export function exportToPlaintext(items: RankingItem[]): string {
  const ranked = sortByRank(items);
  return ranked.map((item, index) => `${index + 1}. ${item.name}`).join('\n');
}

/**
 * Copy to clipboard
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}


